import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FileWarning, Search, Map, MapPin } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Standalone embeddable widget (rendered WITHOUT the site layout).
 * Buttons open the full site in a new tab so it works inside any iframe.
 * Privacy-safe: shows no issue data — only entry points.
 */
export default function EmbedTracker() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const compact = (searchParams.get('mode') || 'full') === 'compact';
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/network/tracker/${slug}`).then((r) => setConfig(r.data)).catch(() => setError(true));
    if (!compact) api.get('/network/stats').then((r) => setStats(r.data)).catch(() => {});
  }, [slug, compact]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const reportUrl = `${origin}/issue-tracker/report?tracker=${slug}`;

  if (error) {
    return (
      <div className="min-h-screen bg-[#1a2744] flex items-center justify-center p-6" data-testid="embed-inactive">
        <p className="text-white/80 text-sm text-center">This tracker is not active yet. Visit <a className="underline text-[#C9A84C]" href={`${origin}/tracker-network`} target="_blank" rel="noopener noreferrer">the Public Issue Tracker App (PITA) Network</a> to learn more.</p>
      </div>
    );
  }
  if (!config) return <div className="min-h-screen bg-[#1a2744]" />;

  return (
    <div className="min-h-screen bg-[#1a2744] p-4 sm:p-6 font-sans" data-testid="embed-widget">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-[#C9A84C]" />
          <p className="text-[10px] font-black uppercase tracking-widest text-[#C9A84C]" data-testid="embed-kicker">{config.name}</p>
        </div>
        <h1 className="text-white font-bold text-xl sm:text-2xl leading-tight mt-1" data-testid="embed-title">{config.title}</h1>
        <p className="text-[#C9A84C] text-sm font-bold mt-1" data-testid="embed-tagline">{config.tagline}</p>
        <p className="text-slate-300 text-xs mt-2 leading-relaxed">Report local concerns, get a tracking number, and follow what happens next. Your name, contact details, and exact address are never shown publicly.</p>

        <div className="mt-4 space-y-2">
          <a href={reportUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 h-11 rounded-xl bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] text-sm font-bold transition-colors" data-testid="embed-report-btn">
            <FileWarning className="h-4 w-4" /> Report an Issue
          </a>
          <div className="grid grid-cols-2 gap-2">
            <a href={`${origin}/issue-tracker/track`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 h-10 rounded-xl border border-white/30 text-white text-xs font-bold hover:bg-white/10 transition-colors" data-testid="embed-track-btn">
              <Search className="h-3.5 w-3.5" /> Track My Issue
            </a>
            <a href={`${origin}/map`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 h-10 rounded-xl border border-white/30 text-white text-xs font-bold hover:bg-white/10 transition-colors" data-testid="embed-map-btn">
              <Map className="h-3.5 w-3.5" /> Public Map
            </a>
          </div>
        </div>

        {!compact && stats && (
          <div className="mt-4 grid grid-cols-3 gap-2" data-testid="embed-stats">
            {[['Tracked', stats.total_tracked], ['Resolved', stats.resolved], ['Open', stats.open]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-white/5 border border-white/10 p-2 text-center">
                <p className="text-lg font-black text-[#C9A84C]">{v}</p>
                <p className="text-[10px] text-slate-300 font-semibold">{l}</p>
              </div>
            ))}
          </div>
        )}

        <p className="mt-4 text-[10px] text-slate-400 text-center" data-testid="embed-powered-by">
          Powered by the <a className="underline" href={`${origin}/tracker-network`} target="_blank" rel="noopener noreferrer">Public Issue Tracker App (PITA) Network</a> · Not an official City service
        </p>
      </div>
    </div>
  );
}
